/**
 * AIContentWriter - Auto AI Content Generation Component
 * 
 * A reusable component that automatically generates professional content
 * based on the section type without requiring user input.
 * 
 * Usage:
 * <AIContentWriter
 *   onContentGenerated={(content) => handleChange({ target: { name: 'paragraph', value: content } })}
 *   contentType="website_description"
 *   context="This is for the download app section description"
 * />
 */

import React, { useState, useEffect } from 'react';
import { generateText, type ContentType } from '@/services/openai.service';
import { toast } from 'react-toastify';
// @ts-ignore - GIF import
import aiIcon from '@/assets/images/ai-icon.gif';

interface AIContentWriterProps {
    onContentGenerated: (content: string) => void;
    contentType?: ContentType;
    placeholder?: string;
    context?: string;
    buttonLabel?: string;
    maxTokens?: number;
    temperature?: number;
}

const AIContentWriter: React.FC<AIContentWriterProps> = ({
    onContentGenerated,
    contentType = 'website_description',
    placeholder = 'Explain what content you want for your website...',
    context,
    buttonLabel = 'AI Writer',
    maxTokens = 500,
    temperature = 0.7,
}) => {
    const [showWriter, setShowWriter] = useState<boolean>(false);
    const [storeName, setStoreName] = useState<string>('');
    const [location, setLocation] = useState<string>('');
    const [generatedContent, setGeneratedContent] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [hasGenerated, setHasGenerated] = useState<boolean>(false);

    // Auto-generate prompt based on content type and context
    const generateAutoPrompt = (): string => {
        const contextText = context ? ` Context: ${context}.` : '';
        const storeText = storeName.trim() ? ` For a business/store named "${storeName}".` : '';
        const locationText = location.trim() ? ` Located in ${location}.` : '';
        const noMarkdownInstruction = ` IMPORTANT: Return only plain text without any markdown symbols, formatting characters (such as *, #, -, _), bullet points, or special characters. Use only regular text. Keep the content concise - random 1 liner or a random 2 or 3 liner only.`;

        const prompts: Record<ContentType, string> = {
            website_description: `Write a professional and engaging website description that highlights key features and benefits.${contextText}${storeText}${locationText} Make it compelling and customer-focused.${noMarkdownInstruction}`,
            product_description: `Write a detailed and persuasive product description that emphasizes benefits, features, and value proposition.${contextText}${storeText}${locationText} Make it appealing to potential customers.${noMarkdownInstruction}`,
            hero_section: `Write an impactful hero section text with a strong headline and compelling call-to-action.${contextText}${storeText}${locationText} Keep it concise and attention-grabbing.${noMarkdownInstruction}`,
            about_us: `Write an authentic and engaging "About Us" section that tells the story of the business, its mission, and values.${contextText}${storeText}${locationText} Make it personal and relatable.${noMarkdownInstruction}`,
            testimonial: `Write a testimonial introduction section paragraph content that highlights key benefits.${contextText}${storeText}${locationText} Make it compelling and customer-focused.${noMarkdownInstruction}`,
            review: `Write a generic and balanced customer review in 2 liner or 20 words that highlights both positive aspects and specific benefits experienced.${contextText}${storeText}${locationText} Make it realistic and helpful.${noMarkdownInstruction}`,
            faq: `Write a comprehensive FAQ entry that clearly answers a common question with helpful and detailed information.${contextText}${storeText}${locationText} Make it informative and easy to understand.${noMarkdownInstruction}`,
            call_to_action: `Write a compelling call-to-action that motivates users to take a specific action.${contextText}${storeText}${locationText} Make it urgent and persuasive.${noMarkdownInstruction}`,
            tagline: `Write a catchy and memorable tagline that captures the essence and value proposition.${contextText}${storeText}${locationText} Keep it short, impactful, and brand-aligned.${noMarkdownInstruction}`,
            meta_description: `Write an SEO-friendly meta description that accurately summarizes the page content and includes relevant keywords.${contextText}${storeText}${locationText} Keep it between 150-160 characters.${noMarkdownInstruction}`,
            blog_post: `Write an engaging blog post introduction that hooks the reader and introduces the topic.${contextText}${storeText}${locationText} Make it interesting and valuable.${noMarkdownInstruction}`,
            announcement: `Write a clear and engaging announcement that communicates important news or updates.${contextText}${storeText}${locationText} Make it informative and exciting.${noMarkdownInstruction}`,
            feature_highlight: `Write a compelling feature highlight that explains the feature's benefits and why it matters to users.${contextText}${storeText}${locationText} Make it benefit-focused and clear.${noMarkdownInstruction}`,
            newsletter: `Write an engaging newsletter introduction that welcomes subscribers and previews the content.${contextText}${storeText}${locationText} Make it warm and informative.${noMarkdownInstruction}`,
            email_subject: `Write a compelling email subject line that grabs attention and encourages opens.${contextText}${storeText}${locationText} Keep it concise and intriguing.${noMarkdownInstruction}`,
            social_media_post: `Write an engaging social media post that is shareable, relatable, and encourages interaction.${contextText}${storeText}${locationText} Make it authentic and engaging.${noMarkdownInstruction}`,
            terms_and_conditions: `Write a clear and comprehensive terms and conditions section that is legally sound and user-friendly.${contextText}${storeText}${locationText} Make it professional and understandable.${noMarkdownInstruction}`,
            privacy_policy: `Write a clear and transparent privacy policy section that builds trust and complies with regulations.${contextText}${storeText}${locationText} Make it professional and reassuring.${noMarkdownInstruction}`,
            general: `Write professional and engaging content that fits the context and purpose.${contextText}${storeText}${locationText} Make it relevant and valuable.${noMarkdownInstruction}`,
            mobile_app_description: `Write a professional and engaging mobile app description that highlights key features and benefits.${contextText}${storeText}${locationText} Make it compelling and customer-focused.${noMarkdownInstruction}`,
        };

        return prompts[contentType] || prompts.general;
    };

    const handleGenerate = async () => {
        setIsGenerating(true);

        try {
            const prompt = generateAutoPrompt();

            const result = await generateText({
                prompt: prompt,
                type: contentType as ContentType,
                context: context,
                storeName: storeName.trim() || undefined,
                location: location.trim() || undefined,
                maxTokens: maxTokens,
                temperature: temperature,
            });

            setGeneratedContent(result.content);
            setHasGenerated(true);
            toast.success('Content generated successfully!');
        } catch (error: any) {
            console.error('Error generating content:', error);
            toast.error(error.message || 'Failed to generate content. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    // Auto-generate when writer panel opens for the first time
    useEffect(() => {
        if (showWriter && !hasGenerated && !isGenerating) {
            setIsGenerating(true);
            // Use setTimeout to ensure state update is processed before async call
            setTimeout(() => {
                handleGenerate();
            }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showWriter]);

    const handleUseContent = () => {
        if (generatedContent.trim()) {
            onContentGenerated(generatedContent);
            toast.success('Content applied successfully!');
            setShowWriter(false);
        } else {
            toast.error('No content to apply');
        }
    };

    const handleReset = () => {
        setStoreName('');
        setLocation('');
        setGeneratedContent('');
        setHasGenerated(false);
        setShowWriter(false);
    };

    const getContentTypeLabel = (type: ContentType): string => {
        const labels: Record<ContentType, string> = {
            website_description: 'Website Description',
            product_description: 'Product Description',
            hero_section: 'Hero Section',
            about_us: 'About Us',
            testimonial: 'Testimonial',
            review: 'Review',
            faq: 'FAQ',
            call_to_action: 'Call to Action',
            tagline: 'Tagline',
            meta_description: 'Meta Description',
            blog_post: 'Blog Post',
            announcement: 'Announcement',
            feature_highlight: 'Feature Highlight',
            newsletter: 'Newsletter',
            email_subject: 'Email Subject',
            social_media_post: 'Social Media Post',
            terms_and_conditions: 'Terms & Conditions',
            privacy_policy: 'Privacy Policy',
            general: 'General Content',
            mobile_app_description: 'Mobile App Description',
        };
        return labels[type];
    };

    return (
        <>
            {/* AI Writer Toggle Link */}
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    setShowWriter(!showWriter);
                }}
                className="ai-writer-btn text-decoration-none d-flex align-items-center gap-2"
                style={{ fontSize: '0.875rem' }}
            >
                <img 
                    src={aiIcon} 
                    alt="AI Icon" 
                    style={{ width: '25px', height: '25px', objectFit: 'contain' }}
                    onError={(e) => {
                        // Fallback if image doesn't exist
                        e.currentTarget.style.display = 'none';
                    }}
                />
                <span className="rainbow_text_animated">Ask AI</span>
            </a>
            
            {/* AI Content Writer Panel - Rendered in document flow after label row */}
            {showWriter && (
                <div className="card border-primary mb-3" style={{ marginTop: '8px', width: '100%', flexBasis: '100%', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                            <i className="fas fa-robot me-2"></i>
                            AI Content Writer - {getContentTypeLabel(contentType)}
                        </h6>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setShowWriter(false)}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="card-body">
                        {/* Auto-generating State */}
                        {(isGenerating || (showWriter && !hasGenerated && !generatedContent)) && (
                            <div className="text-center py-4">
                                <div className="mb-3">
                                    <i className="fas fa-spinner fa-spin text-primary" style={{ fontSize: '2.5rem' }}></i>
                                </div>
                                <h6 className="text-primary mb-2">
                                    <strong>Auto-generating content...</strong>
                                </h6>
                                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                    The AI is creating content based on the section type: {getContentTypeLabel(contentType)}
                                </p>
                            </div>
                        )}

                        {/* Optional Fields: Store Name and Location */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    Store Name <span className="text-muted">(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={storeName}
                                    onChange={(e) => setStoreName(e.target.value)}
                                    placeholder="e.g., TechHub Store"
                                    disabled={isGenerating}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">
                                    Location <span className="text-muted">(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="e.g., New York, USA"
                                    disabled={isGenerating}
                                />
                            </div>
                        </div>
                        <small className="text-muted d-block mb-3">
                            <i className="fas fa-info-circle me-1"></i>
                            Optional: Add store name and location to personalize the auto-generated content
                        </small>

                        {/* Regenerate Button */}
                        {hasGenerated && !isGenerating && (
                            <button
                                type="button"
                                className="btn btn-primary w-100 mb-3"
                                onClick={handleGenerate}
                                disabled={isGenerating}
                            >
                                <i className="fas fa-sync me-2"></i>
                                Re-generate Content
                            </button>
                        )}

                        {/* Generated Content Preview */}
                        {generatedContent && (
                            <div className="generated-content-preview">
                                <div
                                    className="border rounded p-3 bg-light mb-3"
                                    style={{
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: '1.6',
                                        fontSize: '14px',
                                        color: '#333'
                                    }}
                                >
                                    {generatedContent}
                                </div>

                                {/* Action Buttons */}
                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-success flex-grow-1"
                                        onClick={handleUseContent}
                                    >
                                        <i className="fas fa-check me-2"></i>
                                        Use this content
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={handleReset}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Info Alert */}
                        {/* <div className="alert alert-info mt-3 mb-0">
                            <small>
                                <i className="fas fa-info-circle me-1"></i>
                                <strong>Auto-Generation:</strong> Content is automatically generated based on the section type ({getContentTypeLabel(contentType)}).
                                {context && ` Context: ${context}`}
                                {!hasGenerated && !isGenerating && ' Click the AI Writer link to generate content automatically.'}
                            </small>
                        </div> */}
                    </div>
                </div>
            )}
        </>
    );
};

export default AIContentWriter;

